import React from 'react'
import {useTranslation} from 'react-i18next'
import {PhotoUploader} from '../../domain/entities/InvitationCode.ts'
import {PageOption} from '../agencyDetail/unifyCodeRows.ts'
import IconClose from '../../assets/icons/close.svg?react'
import IconChevronDown from '../../assets/icons/chevron-down.svg?react'
import IconBookSingle from '../../assets/icons/book-single.svg?react'
import IconBooksMulti from '../../assets/icons/books-multi.svg?react'
import IconPage from '../../assets/icons/page.svg?react'

// The pre-form steps of "Asignar TripBook" (AssignInvitationCodeDialog) and the
// group-only controls of its form: how many TripBooks, which kind of "varios",
// number of copies and who uploads the photos.

export type TripbookQuantity = 'one' | 'many'
// same: one code whose TripBook is printed `copies` times.
// each: one code per traveler, all on the same trip (backend groupId).
export type GroupGiftType = 'same' | 'each'

export const MIN_GROUP_SIZE = 2
export const MAX_GROUP_SIZE = 99

const SELECTED_BORDER = 'border-neutralBlack-900 shadow-[inset_0_0_0_.5px_theme(colors.neutralBlack.900)]'

export const DialogHeader: React.FC<{title: string; onClose: () => void; onBack?: () => void}> = ({title, onClose, onBack}) => {
    const {t} = useTranslation('dashboard')
    return (
        <div className="flex items-center gap-2.5 px-[26px] pt-6">
            {onBack && (
                <button type="button" onClick={onBack} aria-label={t('dialogs.back')}
                        className="flex-none w-9 h-9 rounded-full text-neutralBlack-700 flex items-center justify-center hover:bg-neutralBlack-100">
                    <IconChevronDown className="w-[11px] h-[11px] rotate-90"/>
                </button>
            )}
            <h2 className="flex-1 min-w-0 text-[21px] font-bold leading-[1.25] tracking-[-0.015em] text-neutralBlack-900">{title}</h2>
            <button type="button" onClick={onClose} aria-label={t('cancel')}
                    className="flex-none w-9 h-9 rounded-full bg-neutralBlack-100 text-neutralBlack-900 flex items-center justify-center hover:brightness-95">
                <IconClose className="size-[18px]"/>
            </button>
        </div>
    )
}

const ContinueButton: React.FC<{onClick: () => void}> = ({onClick}) => {
    const {t} = useTranslation('dashboard')
    return (
        <div className="flex justify-center px-[26px] pb-8">
            <button type="button" onClick={onClick}
                    className="h-[50px] min-w-[220px] px-6 rounded-full bg-neutralBlack-900 text-white text-base font-semibold hover:brightness-110">
                {t('dialogs.continue')}
            </button>
        </div>
    )
}

const InfoNote: React.FC<{children: React.ReactNode}> = ({children}) => (
    <div className="flex items-start gap-2.5">
        <span aria-hidden="true" className="flex-none w-[15px] h-[15px] mt-px rounded-full border-[1.2px] border-neutralBlack-400 text-neutralBlack-500 text-[9.5px] font-bold leading-[13px] text-center">i</span>
        <span className="text-[12.5px] leading-[1.5] text-neutralBlack-400">{children}</span>
    </div>
)

const RadioDot: React.FC<{isOn: boolean; size: 'sm' | 'md'}> = ({isOn, size}) => (
    <span className={`flex-none rounded-full flex items-center justify-center ${size === 'md' ? 'w-5 h-5 mt-px' : 'w-[18px] h-[18px]'} ${isOn ? 'bg-neutralBlack-900' : 'border-[1.8px] border-neutralBlack-300'}`}>
        {isOn && <span className={`rounded-full bg-white ${size === 'md' ? 'w-2 h-2' : 'w-[7px] h-[7px]'}`}/>}
    </span>
)

export const QuantityStep: React.FC<{
    value: TripbookQuantity
    onChange: (value: TripbookQuantity) => void
    onContinue: () => void
    onClose: () => void
}> = ({value, onChange, onContinue, onClose}) => {
    const {t} = useTranslation('dashboard')
    const options: {key: TripbookQuantity; label: string}[] = [
        {key: 'one', label: t('dialogs.quantityOne')},
        {key: 'many', label: t('dialogs.quantityMany')},
    ]
    return (
        <>
            <DialogHeader title={t('dialogs.assignTripbookTitle')} onClose={onClose}/>
            <div className="px-[26px] pt-[30px] pb-[34px] flex flex-col gap-[18px]">
                <div className="flex flex-col gap-1.5">
                    <span className="text-[17px] font-semibold leading-[1.35] tracking-[-0.01em] text-neutralBlack-900">{t('dialogs.quantityQuestion')}</span>
                    <InfoNote>{t('dialogs.quantityNote')}</InfoNote>
                </div>
                <div role="radiogroup" className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
                    {options.map((o) => {
                        const isOn = value === o.key
                        const Icon = o.key === 'one' ? IconBookSingle : IconBooksMulti
                        return (
                            <button key={o.key} type="button" role="radio" aria-checked={isOn} onClick={() => onChange(o.key)}
                                    className={`flex flex-col items-center justify-center gap-[18px] px-5 pt-[30px] pb-6 rounded-2xl bg-white text-center border-[1.5px] transition-colors duration-150 ease-out ${isOn ? SELECTED_BORDER : 'border-neutralBlack-200 hover:border-neutralBlack-400'}`}>
                                <Icon aria-hidden="true"
                                      className={`flex-none text-neutralBlack-900 transition-opacity duration-150 ease-out ${o.key === 'many' ? 'w-[98px] h-[98px] -my-[11px]' : 'w-[76px] h-[76px]'} ${isOn ? 'opacity-100' : 'opacity-[.22]'}`}/>
                                <span className={`max-w-[190px] text-[14.5px] leading-[1.35] text-balance ${isOn ? 'font-semibold text-neutralBlack-900' : 'font-medium text-neutralBlack-400'}`}>
                                    {o.label}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </div>
            <ContinueButton onClick={onContinue}/>
        </>
    )
}

export const GiftTypeStep: React.FC<{
    value: GroupGiftType
    onChange: (value: GroupGiftType) => void
    onContinue: () => void
    onBack: () => void
    onClose: () => void
}> = ({value, onChange, onContinue, onBack, onClose}) => {
    const {t} = useTranslation('dashboard')
    const options: {key: GroupGiftType; label: string; sub: string}[] = [
        {key: 'same', label: t('dialogs.giftTypeSame'), sub: t('dialogs.giftTypeSameHelp')},
        {key: 'each', label: t('dialogs.giftTypeEach'), sub: t('dialogs.giftTypeEachHelp')},
    ]
    return (
        <>
            <DialogHeader title={t('dialogs.assignTripbookTitle')} onClose={onClose} onBack={onBack}/>
            <div className="px-[26px] pt-[30px] pb-[34px] flex flex-col gap-[18px]">
                <span className="text-[17px] font-semibold leading-[1.35] tracking-[-0.01em] text-neutralBlack-900">{t('dialogs.giftTypeQuestion')}</span>
                <div role="radiogroup" className="flex flex-col gap-3">
                    {options.map((o) => {
                        const isOn = value === o.key
                        return (
                            <button key={o.key} type="button" role="radio" aria-checked={isOn} onClick={() => onChange(o.key)}
                                    className={`flex items-start gap-3.5 py-[18px] pr-[18px] pl-4 rounded-2xl bg-white text-left border-[1.5px] transition-colors duration-150 ease-out ${isOn ? SELECTED_BORDER : 'border-neutralBlack-200 hover:border-neutralBlack-400'}`}>
                                <RadioDot isOn={isOn} size="md"/>
                                <span className="flex-1 min-w-0 flex flex-col gap-[5px]">
                                    <span className={`text-[14.5px] leading-[1.35] ${isOn ? 'font-semibold text-neutralBlack-900' : 'font-medium text-neutralBlack-500'}`}>{o.label}</span>
                                    <span className={`text-[12.5px] leading-[1.5] text-pretty ${isOn ? 'text-neutralBlack-600' : 'text-neutralBlack-400'}`}>{o.sub}</span>
                                </span>
                            </button>
                        )
                    })}
                </div>
            </div>
            <ContinueButton onClick={onContinue}/>
        </>
    )
}

export const CountStepper: React.FC<{label: string; value: number; onChange: (value: number) => void}> = ({label, value, onChange}) => {
    const {t} = useTranslation('dashboard')
    const clamp = (n: number) => Math.max(MIN_GROUP_SIZE, Math.min(MAX_GROUP_SIZE, n))
    const isAtMin = value <= MIN_GROUP_SIZE
    const isAtMax = value >= MAX_GROUP_SIZE
    const stepClass = (isDisabled: boolean) =>
        `w-8 h-8 rounded-full bg-neutralBlack-100 text-[17px] font-medium leading-none ${isDisabled ? 'text-neutralBlack-300 cursor-not-allowed' : 'text-neutralBlack-900 hover:bg-neutralBlack-200'}`
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-[13px] font-semibold text-neutralBlack-900">{label}</span>
            <div className="flex items-center gap-1 p-1 rounded-full border-[1.5px] border-neutralBlack-200">
                <button type="button" aria-label={t('dialogs.decrease')} disabled={isAtMin} onClick={() => onChange(clamp(value - 1))} className={stepClass(isAtMin)}>−</button>
                <input
                    inputMode="numeric"
                    aria-label={label}
                    value={String(value)}
                    onChange={(e) => {
                        const parsed = parseInt(e.target.value.replace(/\D/g, ''), 10)
                        onChange(isNaN(parsed) ? MIN_GROUP_SIZE : Math.min(MAX_GROUP_SIZE, parsed))
                    }}
                    onBlur={() => onChange(clamp(value))}
                    className="w-[38px] h-8 border-0 outline-none bg-transparent text-center text-[15px] font-semibold text-neutralBlack-900"
                />
                <button type="button" aria-label={t('dialogs.increase')} disabled={isAtMax} onClick={() => onChange(clamp(value + 1))} className={stepClass(isAtMax)}>+</button>
            </div>
        </div>
    )
}

export const PhotoUploaderPicker: React.FC<{value: PhotoUploader; onChange: (value: PhotoUploader) => void}> = ({value, onChange}) => {
    const {t} = useTranslation('dashboard')
    const options: {key: PhotoUploader; label: string}[] = [
        {key: 'AGENCY', label: t('dialogs.photoUploaderAgency')},
        {key: 'OTHER', label: t('dialogs.photoUploaderOther')},
    ]
    return (
        <div className="flex flex-col gap-2.5">
            <span className="text-[13px] font-semibold text-neutralBlack-900">{t('dialogs.photoUploaderQuestion')}</span>
            <div role="radiogroup" className="flex gap-2.5 flex-wrap">
                {options.map((o) => {
                    const isOn = value === o.key
                    return (
                        <button key={o.key} type="button" role="radio" aria-checked={isOn} onClick={() => onChange(o.key)}
                                className={`flex-1 basis-40 flex items-center gap-2.5 h-12 px-4 rounded-[14px] bg-white text-sm border-[1.5px] transition-colors duration-150 ease-out ${isOn ? `font-semibold text-neutralBlack-900 ${SELECTED_BORDER}` : 'font-medium text-neutralBlack-500 border-neutralBlack-200 hover:border-neutralBlack-400'}`}>
                            <RadioDot isOn={isOn} size="sm"/>
                            <span>{o.label}</span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

// "Paginación del TripBook": one chip per pagination the agency's packs come
// in, each with the codes still available at those pages.
export const PagesPicker: React.FC<{options: PageOption[]; value: number | undefined; onChange: (pages: number) => void}> = ({options, value, onChange}) => {
    const {t} = useTranslation('dashboard')
    return (
        <div className="flex flex-col gap-2.5">
            <span className="text-[13px] font-semibold text-neutralBlack-900">{t('dialogs.pagesLabel')}</span>
            <div role="radiogroup" className="flex flex-wrap gap-2">
                {options.map((o) => {
                    const isOn = value === o.pages
                    return (
                        <button key={o.pages} type="button" role="radio" aria-checked={isOn} onClick={() => onChange(o.pages)}
                                className={`w-[104px] flex flex-col items-start gap-2 pt-[11px] px-3 pb-2.5 rounded-xl bg-white text-left border-[1.5px] transition-colors duration-150 ease-out ${isOn ? SELECTED_BORDER : 'border-neutralBlack-200 hover:border-neutralBlack-400'}`}>
                            <IconPage aria-hidden="true" className={`w-[17px] h-5 ${isOn ? 'text-neutralBlack-900' : 'text-neutralBlack-400'}`}/>
                            <span className="flex items-baseline gap-[5px]">
                                <span className="text-[17px] font-bold leading-none tracking-[-0.02em] text-neutralBlack-900">{o.pages}</span>
                                <span className="text-[11.5px] text-neutralBlack-500">{t('dialogs.pagesUnit')}</span>
                            </span>
                            <span className={`inline-flex items-center h-[19px] px-[7px] rounded-full text-[10.5px] font-semibold whitespace-nowrap ${o.available > 0 ? 'bg-[#E3F3EA] text-[#16603F]' : 'bg-neutralBlack-100 text-neutralBlack-500'}`}>
                                {t('dialogs.pagesAvailable', {count: o.available})}
                            </span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export const Switch: React.FC<{checked: boolean; onChange: (checked: boolean) => void; label: string}> = ({checked, onChange, label}) => (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}
            className={`relative flex-none w-[46px] h-7 rounded-full transition-colors duration-200 ease-out ${checked ? 'bg-neutralBlack-900' : 'bg-neutralBlack-200'}`}>
        <span className={`absolute top-[3px] left-[3px] w-[22px] h-[22px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,.2)] transition-transform duration-200 ease-out ${checked ? 'translate-x-[18px]' : ''}`}/>
    </button>
)
